/* ============================================================
   Lilly & Mae — Size Guide & Size Finder
   - Renders the size charts, size finder and measuring guide.
   - Never reads or changes the product's variant selector.
   - Recommends a size from the product's own measurements
     (metafield custom.size_measurements) or, without those,
     from its category size guide (dresses, tops, bottoms, sets...).
   ============================================================ */
(function () {
  'use strict';

  if (window.LMSizeGuide) {
    window.LMSizeGuide.initAll();
    return;
  }

  var MEASURES = ['bust', 'waist', 'hip'];
  var STORE_KEY = 'lm-sg-v1';
  var MIN_CM = 50;
  var MAX_CM = 200;
  var HEIGHT_MIN = 120;
  var HEIGHT_MAX = 210;
  // Shoulder height is roughly 82% of body height (standard anthropometric ratio).
  var SHOULDER_RATIO = 0.82;
  var uid = 0;

  var EXTRA_LABELS = {
    length: 'Length', sleeve: 'Sleeve', shoulder: 'Shoulder', inside_leg: 'Inside leg',
    inseam: 'Inside leg', rise: 'Rise', thigh: 'Thigh', hem: 'Hem', underbust: 'Underbust'
  };

  /* ---------- helpers ---------- */
  function store(value) {
    try {
      if (value === undefined) return JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}');
      window.localStorage.setItem(STORE_KEY, JSON.stringify(value));
    } catch (e) { return {}; }
    return value;
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function tpl(str, data) {
    return String(str || '').replace(/\{(\w+)\}/g, function (_, key) {
      return data[key] == null ? '' : data[key];
    });
  }

  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function isRange(v) { return Array.isArray(v) && v.length === 2 && isNum(v[0]) && isNum(v[1]); }
  function hasValue(v) { return isNum(v) || isRange(v); }

  function fmtNum(cm, unit) {
    if (unit === 'in') return String(Math.round((cm / 2.54) * 2) / 2);
    return String(Math.round(cm * 10) / 10);
  }

  function fmt(value, unit) {
    if (isRange(value)) return fmtNum(value[0], unit) + '–' + fmtNum(value[1], unit);
    if (isNum(value)) return fmtNum(value, unit);
    return '–';
  }

  function joinList(items, andWord) {
    if (items.length < 2) return items.join('');
    return items.slice(0, -1).join(', ') + ' ' + andWord + ' ' + items[items.length - 1];
  }

  function labelFor(key, S) {
    if (S[key]) return S[key];
    if (EXTRA_LABELS[key]) return EXTRA_LABELS[key];
    var s = String(key).replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /* ---------- product measurement logic ---------- */
  function normaliseFit(fit) {
    if (!fit || typeof fit !== 'object' || !Array.isArray(fit.sizes)) return null;
    var sizes = fit.sizes.filter(function (s) { return s && s.size != null; });
    if (!sizes.length) return null;
    return {
      basis: fit.basis === 'garment' ? 'garment' : 'body',
      ease: fit.ease && typeof fit.ease === 'object' ? fit.ease : {},
      sizes: sizes,
      note: typeof fit.note === 'string' ? fit.note : ''
    };
  }

  // Measures present for every size in the product data.
  function measuredKeys(fit) {
    if (!fit) return [];
    return MEASURES.filter(function (m) {
      return fit.sizes.every(function (s) { return hasValue(s[m]); });
    });
  }

  // Measures that can drive a recommendation.
  // body basis: any measured key. garment basis: only keys with an ease allowance.
  function usableKeys(fit) {
    return measuredKeys(fit).filter(function (m) {
      if (fit.basis === 'body') return true;
      return isNum(fit.ease[m]) && fit.sizes.every(function (s) { return isNum(s[m]); });
    });
  }

  function extraKeys(fit) {
    if (!fit) return [];
    var keys = [];
    fit.sizes.forEach(function (s) {
      Object.keys(s).forEach(function (k) {
        if (k === 'size' || MEASURES.indexOf(k) !== -1 || keys.indexOf(k) !== -1) return;
        if (hasValue(s[k])) keys.push(k);
      });
    });
    return keys;
  }

  // Largest body measurement a size accommodates.
  function upper(fit, s, m) {
    var v = s[m];
    if (fit.basis === 'garment') return v - fit.ease[m];
    return isRange(v) ? Math.max(v[0], v[1]) : v;
  }

  function lower(fit, s, m) {
    var v = s[m];
    if (fit.basis === 'body' && isRange(v)) return Math.min(v[0], v[1]);
    return null;
  }

  function recommend(fit, entered) {
    var keys = usableKeys(fit).filter(function (m) { return isNum(entered[m]); });
    if (!keys.length) return null;

    var perMeasure = {};
    var over = [];
    keys.forEach(function (m) {
      var idx = -1;
      for (var i = 0; i < fit.sizes.length; i++) {
        if (upper(fit, fit.sizes[i], m) >= entered[m]) { idx = i; break; }
      }
      if (idx === -1) over.push(m);
      perMeasure[m] = idx;
    });

    if (over.length) return { type: 'over', over: over, keys: keys };

    var max = -1, min = Infinity;
    keys.forEach(function (m) {
      max = Math.max(max, perMeasure[m]);
      min = Math.min(min, perMeasure[m]);
    });

    var roomy = max === 0 && keys.every(function (m) {
      var lo = lower(fit, fit.sizes[0], m);
      return lo != null && entered[m] < lo;
    });

    return { type: 'size', index: max, minIndex: min, perMeasure: perMeasure, keys: keys, roomy: roomy };
  }

  // The category size guide, in the same shape as product data.
  function chartFit(cat) {
    return cat && Array.isArray(cat.sizes) ? normaliseFit({ basis: 'body', sizes: cat.sizes }) : null;
  }

  /* ---------- component ---------- */
  function SizeGuide(root) {
    this.root = root;
    this.id = 'lm-sg-' + (++uid);
    this.config = readJSON(root, '[data-lm-sg-config]') || {};
    this.charts = readJSON(root, '[data-lm-sg-charts]') || { conversions: {}, body: {}, categories: {} };
    this.S = readJSON(root, '[data-lm-sg-strings]') || {};
    this.fit = normaliseFit(readJSON(root, '[data-lm-sg-product]'));
    this.trigger = root.querySelector('[data-lm-sg-open]');
    this.dialog = root.querySelector('[data-lm-sg-dialog]');
    if (!this.trigger || !this.dialog) return;

    var cats = Object.keys(this.charts.categories || {});
    this.fixedCategory = cats.indexOf(this.config.chart) !== -1;
    this.category = this.fixedCategory ? this.config.chart : (cats[0] || '');
    this.saved = store();
    this.unit = this.saved.unit === 'in' ? 'in' : 'cm';
    this.tab = 'guide';

    // Move the dialog out of the product form so it can never submit or alter it.
    this.dialog.setAttribute('data-lm-sg-owner', this.id);
    document.body.appendChild(this.dialog);

    this.sheet = this.dialog.querySelector('[data-lm-sg-sheet]');
    this.renderShell();
    this.bind();
  }

  SizeGuide.prototype.renderShell = function () {
    var S = this.S, id = this.id;
    var tabs = [['guide', S.tabGuide], ['finder', S.tabFinder], ['measure', S.tabMeasure]];

    this.dialog.setAttribute('aria-labelledby', id + '-title');
    this.dialog.removeAttribute('aria-label');

    this.sheet.innerHTML =
      '<header class="lm-sg-head">' +
        '<p class="lm-sg-eyebrow">' + esc(S.eyebrow) + '</p>' +
        '<h2 class="lm-sg-title" id="' + id + '-title">' + esc(S.title) + '</h2>' +
        (this.config.productTitle ? '<p class="lm-sg-product">' + esc(this.config.productTitle) + '</p>' : '') +
        '<button type="button" class="lm-sg-close" data-lm-close aria-label="' + esc(S.close) + '">' +
          '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>' +
        '</button>' +
        '<div class="lm-sg-tabs" role="tablist">' +
          tabs.map(function (t) {
            return '<button type="button" role="tab" class="lm-sg-tab" id="' + id + '-tab-' + t[0] + '" data-lm-tab="' + t[0] + '" aria-controls="' + id + '-panel-' + t[0] + '" aria-selected="false" tabindex="-1">' + esc(t[1]) + '</button>';
          }).join('') +
        '</div>' +
      '</header>' +
      '<div class="lm-sg-body" data-lm-body>' +
        tabs.map(function (t) {
          return '<section class="lm-sg-panel" role="tabpanel" id="' + id + '-panel-' + t[0] + '" aria-labelledby="' + id + '-tab-' + t[0] + '" data-lm-panel="' + t[0] + '" tabindex="0" hidden></section>';
        }).join('') +
      '</div>' +
      '<footer class="lm-sg-foot">' + esc(S.help) +
        (this.config.contactUrl ? '<a href="' + esc(this.config.contactUrl) + '">' + esc(S.helpLink) + '</a>' : '') +
      '</footer>';

    this.renderGuide();
    this.renderFinder();
    this.renderMeasure();
    this.selectTab(this.tab, false);
  };

  /* ----- Tab 1: size charts ----- */
  SizeGuide.prototype.renderGuide = function () {
    var S = this.S, unit = this.unit, charts = this.charts, cat = charts.categories[this.category] || {};
    var conv = charts.conversions || {};
    var catFit = chartFit(cat);
    var measures = (cat.measures || MEASURES).filter(function (m) { return catFit && catFit.sizes.every(function (s) { return hasValue(s[m]); }); });
    var self = this;
    var html = '';

    // Controls: category pills only when the product has no category of its own
    html += '<div class="lm-sg-row">' +
      (this.fixedCategory ? '<span></span>' :
        '<div class="lm-sg-pills" role="group" aria-label="' + esc(S.tabGuide) + '">' +
          Object.keys(charts.categories).map(function (key) {
            return '<button type="button" class="lm-sg-pill" data-lm-cat="' + esc(key) + '" aria-pressed="' + (key === self.category) + '">' + esc(charts.categories[key].label) + '</button>';
          }).join('') +
        '</div>') +
      '<div class="lm-sg-units" role="group" aria-label="' + esc(S.units) + '">' +
        '<button type="button" class="lm-sg-unit" data-lm-unit="cm" aria-pressed="' + (unit === 'cm') + '">cm</button>' +
        '<button type="button" class="lm-sg-unit" data-lm-unit="in" aria-pressed="' + (unit === 'in') + '">in</button>' +
      '</div>' +
    '</div>';

    // This piece (only when the product has its own data)
    if (this.fit) {
      var fit = this.fit;
      var keys = measuredKeys(fit).concat(extraKeys(fit));
      html += '<div class="lm-sg-section">' +
        '<h3 class="lm-sg-h3">' + esc(S.thisPiece) + '</h3>' +
        '<p class="lm-sg-lede">' + esc(fit.basis === 'garment' ? S.thisPieceGarment : S.thisPieceBody) + ' (' + unit + ')</p>' +
        '<div class="lm-sg-table-wrap"><table class="lm-sg-table"><thead><tr>' +
          '<th scope="col">' + esc(S.size) + '</th>' +
          keys.map(function (k) { return '<th scope="col">' + esc(labelFor(k, S)) + '</th>'; }).join('') +
        '</tr></thead><tbody>' +
          fit.sizes.map(function (s) {
            return '<tr><th scope="row">' + esc(s.size) + '</th>' +
              keys.map(function (k) { return '<td>' + fmt(s[k], unit) + '</td>'; }).join('') + '</tr>';
          }).join('') +
        '</tbody></table></div>' +
        (fit.note ? '<p class="lm-sg-note">' + esc(fit.note) + '</p>' : '') +
      '</div>';
    }

    if (this.config.fitNotes) {
      html += '<p class="lm-sg-note">' + esc(this.config.fitNotes) + '</p>';
    }

    // Category size guide
    if (catFit) {
      html += '<div class="lm-sg-section">' +
        '<h3 class="lm-sg-h3">' + esc(tpl(S.generalGuide, { label: cat.label || '' })) + '</h3>' +
        '<p class="lm-sg-lede">' + esc(S.generalIntro) + ' (' + unit + ')</p>' +
        '<div class="lm-sg-table-wrap"><table class="lm-sg-table"><thead><tr>' +
          '<th scope="col">' + esc(S.size) + '</th>' +
          measures.map(function (m) { return '<th scope="col">' + esc(S[m]) + '</th>'; }).join('') +
        '</tr></thead><tbody>' +
          catFit.sizes.map(function (s) {
            return '<tr><th scope="row">' + esc(s.size) + '</th>' +
              measures.map(function (m) { return '<td>' + fmt(s[m], unit) + '</td>'; }).join('') + '</tr>';
          }).join('') +
        '</tbody></table></div>' +
        (cat.note ? '<p class="lm-sg-note">' + esc(cat.note) + '</p>' : '') +
      '</div>';
    }

    // Conversions (first column is our size)
    var regions = Object.keys(conv);
    var rows = regions.length ? (conv[regions[0]] || []) : [];
    if (rows.length) {
      html += '<div class="lm-sg-section">' +
        '<h3 class="lm-sg-h3">' + esc(S.conversions) + '</h3>' +
        '<p class="lm-sg-lede">' + esc(S.conversionsIntro) + '</p>' +
        '<div class="lm-sg-table-wrap"><table class="lm-sg-table"><thead><tr>' +
          regions.map(function (r) { return '<th scope="col">' + esc(r) + '</th>'; }).join('') +
        '</tr></thead><tbody>' +
          rows.map(function (_, i) {
            return '<tr>' + regions.map(function (r, j) {
              var v = esc((conv[r] || [])[i] || '');
              return j === 0 ? '<th scope="row">' + v + '</th>' : '<td>' + v + '</td>';
            }).join('') + '</tr>';
          }).join('') +
        '</tbody></table></div>' +
      '</div>';
    }

    this.panel('guide').innerHTML = html;
  };

  /* ----- Tab 2: size finder ----- */
  SizeGuide.prototype.finderKeys = function () {
    var cat = this.charts.categories[this.category] || {};
    var keys = this.fit ? measuredKeys(this.fit) : [];
    if (!keys.length) keys = (cat.measures || MEASURES).slice();
    if (cat.askHeight) keys.push('height');
    return keys;
  };

  function isMaxi(title) {
    return /\b(maxi|gown|floor[- ]length)\b/i.test(String(title || ''));
  }

  SizeGuide.prototype.renderFinder = function () {
    var S = this.S, id = this.id, saved = this.saved || {};
    var keys = this.finderKeys();
    var hints = { bust: S.hintBust, waist: S.hintWaist, hip: S.hintHip, height: S.hintHeight };

    this.panel('finder').innerHTML =
      '<h3 class="lm-sg-h3">' + esc(S.tabFinder) + (this.charts.categories[this.category] ? ' — ' + esc(this.charts.categories[this.category].label) : '') + '</h3>' +
      '<p class="lm-sg-lede">' + esc(this.fit && usableKeys(this.fit).length ? S.finderIntro : S.finderIntroNoData) + '</p>' +
      '<form class="lm-sg-form" data-lm-form novalidate>' +
        '<div class="lm-sg-fields">' +
          keys.map(function (m) {
            var v = isNum(saved[m]) ? saved[m] : '';
            var isH = m === 'height';
            return '<div class="lm-sg-field">' +
              '<label for="' + id + '-' + m + '">' + esc(S[m]) + (isH ? ' <span class="lm-sg-optional">' + esc(S.optional) + '</span>' : '') + '</label>' +
              '<div class="lm-sg-input">' +
                '<input type="number" inputmode="decimal" step="0.5" min="' + (isH ? HEIGHT_MIN : MIN_CM) + '" max="' + (isH ? HEIGHT_MAX : MAX_CM) + '" id="' + id + '-' + m + '" data-lm-measure="' + m + '" value="' + esc(v) + '" aria-describedby="' + id + '-' + m + '-hint ' + id + '-' + m + '-err" autocomplete="off">' +
                '<span class="lm-sg-input__unit" aria-hidden="true">cm</span>' +
              '</div>' +
              '<p class="lm-sg-hint" id="' + id + '-' + m + '-hint">' + esc(hints[m]) + '</p>' +
              '<p class="lm-sg-error" id="' + id + '-' + m + '-err" aria-live="polite"></p>' +
            '</div>';
          }).join('') +
        '</div>' +
        '<div class="lm-sg-actions">' +
          '<button type="submit" class="lm-sg-submit">' + esc(S.submit) + '</button>' +
          '<p class="lm-sg-privacy">' + esc(S.remember) + '</p>' +
        '</div>' +
      '</form>' +
      '<div class="lm-sg-result" data-lm-result aria-live="polite"></div>';
  };

  SizeGuide.prototype.readInputs = function () {
    var S = this.S, entered = {}, valid = true, any = false;
    var inputs = this.panel('finder').querySelectorAll('[data-lm-measure]');

    Array.prototype.forEach.call(inputs, function (input) {
      var m = input.getAttribute('data-lm-measure');
      var err = document.getElementById(input.getAttribute('aria-describedby').split(' ')[1]);
      var raw = String(input.value || '').replace(',', '.').trim();
      err.textContent = '';
      input.removeAttribute('aria-invalid');
      if (raw === '') return;
      var n = parseFloat(raw);
      var lo = m === 'height' ? HEIGHT_MIN : MIN_CM, hi = m === 'height' ? HEIGHT_MAX : MAX_CM;
      if (!isFinite(n) || n < lo || n > hi) {
        err.textContent = m === 'height' ? S.errorHeight : S.errorRange;
        input.setAttribute('aria-invalid', 'true');
        valid = false;
        return;
      }
      entered[m] = n;
      if (m !== 'height') any = true;
    });

    if (valid && !any) {
      var first = inputs[0];
      if (first) {
        document.getElementById(first.getAttribute('aria-describedby').split(' ')[1]).textContent = S.errorNone;
        first.setAttribute('aria-invalid', 'true');
        first.focus();
      }
      return null;
    }
    return valid ? entered : null;
  };

  SizeGuide.prototype.onSubmit = function () {
    var entered = this.readInputs();
    var out = this.panel('finder').querySelector('[data-lm-result]');
    if (!entered) { out.innerHTML = ''; return; }

    var saved = store();
    MEASURES.concat('height').forEach(function (m) { if (isNum(entered[m])) saved[m] = entered[m]; });
    saved.unit = this.unit;
    store(saved);
    this.saved = saved;

    var fit = this.fit;
    if (fit && usableKeys(fit).length) {
      var result = recommend(fit, entered);
      if (result) { out.innerHTML = this.resultHTML(result, entered, fit, null); return; }
    }
    if (fit && fit.basis === 'garment' && measuredKeys(fit).length) {
      out.innerHTML = this.compareHTML(entered);
      return;
    }
    var cat = this.charts.categories[this.category];
    var catFit = chartFit(cat);
    if (catFit) {
      var r = recommend(catFit, entered);
      if (r) { out.innerHTML = this.resultHTML(r, entered, catFit, cat); return; }
    }
    out.innerHTML = '<p>' + esc(this.S.errorNone) + '</p>';
  };

  SizeGuide.prototype.resultHTML = function (r, entered, fit, cat) {
    var S = this.S;
    var names = function (keys) { return joinList(keys.map(function (k) { return S[k].toLowerCase(); }), S.and); };

    if (r.type === 'over') {
      return '<p class="lm-sg-result__label">' + esc(S.recommendLabel) + '</p>' +
        '<p>' + esc(tpl(S.recommendOver, { list: names(r.over), size: fit.sizes[fit.sizes.length - 1].size })) + '</p>';
    }

    var size = fit.sizes[r.index].size;
    var html = '<p class="lm-sg-result__label">' + esc(S.recommendLabel) + '</p>' +
      '<p class="lm-sg-result__size">' + esc(size) + '</p>' +
      '<p>' + esc(cat ? tpl(S.recommendBasisChart, { label: String(cat.label || '').toLowerCase(), list: names(r.keys) }) : tpl(S.recommendBasis, { list: names(r.keys) })) + '</p>';

    if (r.minIndex !== r.index) {
      var low = r.keys.filter(function (k) { return r.perMeasure[k] === r.minIndex; });
      var high = r.keys.filter(function (k) { return r.perMeasure[k] === r.index; });
      html += '<p>' + esc(tpl(S.recommendSpread, {
        low: names(low), lowSize: fit.sizes[r.minIndex].size,
        high: names(high), highSize: size
      })) + '</p>';
    }
    if (r.roomy) html += '<p>' + esc(S.recommendSmallest) + '</p>';

    var missing = usableKeys(fit).filter(function (k) { return !isNum(entered[k]); });
    if (missing.length) html += '<p>' + esc(tpl(S.recommendMissing, { list: names(missing) })) + '</p>';
    html += this.lengthAdvice(size, entered);
    if (fit.note) html += '<p class="lm-sg-note">' + esc(fit.note) + '</p>';
    if (cat && cat.note) html += '<p class="lm-sg-note">' + esc(cat.note) + '</p>';
    html += '<p class="lm-sg-small">' + esc(S.recommendNote) + '</p>';
    return html;
  };

  // Where the hem falls, from the customer's height and this piece's own length per size.
  // Only calculated when the product has a "length" (shoulder to hem, cm) for that size.
  SizeGuide.prototype.lengthAdvice = function (size, entered) {
    var S = this.S, h = entered.height;
    if (!isNum(h)) return '';
    var row = null;
    if (this.fit) {
      for (var i = 0; i < this.fit.sizes.length; i++) {
        if (String(this.fit.sizes[i].size) === String(size)) { row = this.fit.sizes[i]; break; }
      }
    }
    var len = row && isNum(row.length) ? row.length : null;
    if (len === null) {
      return isMaxi(this.config.productTitle) ? '<p>' + esc(S.lengthNoData) + '</p>' : '';
    }
    var gap = Math.round(h * SHOULDER_RATIO - len);
    var msg;
    if (gap < -4) msg = S.lengthLong;
    else if (gap <= 0) msg = S.lengthFloor;
    else if (gap <= 8) msg = S.lengthSkim;
    else if (gap <= 25) msg = S.lengthAnkle;
    else return '';
    return '<p>' + esc(tpl(msg, { size: size, len: fmtNum(len, 'cm'), height: fmtNum(h, 'cm'), gap: Math.abs(gap) })) + '</p>';
  };

  // Garment measurements without fit allowances: show facts, no recommendation.
  SizeGuide.prototype.compareHTML = function (entered) {
    var S = this.S, fit = this.fit;
    var keys = measuredKeys(fit).filter(function (k) { return isNum(entered[k]) && fit.sizes.every(function (s) { return isNum(s[k]); }); });
    var html = '<p class="lm-sg-result__label">' + esc(S.compareTitle) + '</p>' +
      '<p>' + esc(S.compareIntro) + '</p>';
    if (!keys.length) return html;

    html += '<div class="lm-sg-table-wrap"><table class="lm-sg-table"><thead><tr><th scope="col">' + esc(S.size) + '</th>' +
      keys.map(function (k) { return '<th scope="col">' + esc(S[k]) + ' (cm)</th>'; }).join('') + '</tr></thead><tbody>' +
      fit.sizes.map(function (s) {
        return '<tr><th scope="row">' + esc(s.size) + '</th>' + keys.map(function (k) {
          var diff = Math.round((s[k] - entered[k]) * 10) / 10;
          var cls = diff >= 0 ? 'lm-sg-pos' : 'lm-sg-neg';
          var txt = diff >= 0 ? '+' + diff + ' cm ' + S.compareRoom : Math.abs(diff) + ' cm ' + S.compareShort;
          return '<td>' + fmt(s[k], 'cm') + '<span class="' + cls + '">' + esc(txt) + '</span></td>';
        }).join('') + '</tr>';
      }).join('') + '</tbody></table></div>';
    return html;
  };

  /* ----- Tab 3: how to measure ----- */
  SizeGuide.prototype.renderMeasure = function () {
    var S = this.S;
    this.panel('measure').innerHTML =
      '<h3 class="lm-sg-h3">' + esc(S.tabMeasure) + '</h3>' +
      '<p class="lm-sg-lede">' + esc(S.measureIntro) + '</p>' +
      '<ol class="lm-sg-steps">' +
        '<li><strong>' + esc(S.bust) + '</strong><span>' + esc(S.measureBust) + '</span></li>' +
        '<li><strong>' + esc(S.waist) + '</strong><span>' + esc(S.measureWaist) + '</span></li>' +
        '<li><strong>' + esc(S.hip) + '</strong><span>' + esc(S.measureHip) + '</span></li>' +
      '</ol>' +
      '<p class="lm-sg-note">' + esc(S.measureTip) + '</p>';
  };

  /* ---------- behaviour ---------- */
  SizeGuide.prototype.panel = function (name) {
    return this.sheet.querySelector('[data-lm-panel="' + name + '"]');
  };

  SizeGuide.prototype.selectTab = function (name, focus) {
    this.tab = name;
    var tabs = this.sheet.querySelectorAll('[data-lm-tab]');
    Array.prototype.forEach.call(tabs, function (t) {
      var on = t.getAttribute('data-lm-tab') === name;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      if (on && focus) t.focus();
    });
    Array.prototype.forEach.call(this.sheet.querySelectorAll('[data-lm-panel]'), function (p) {
      p.hidden = p.getAttribute('data-lm-panel') !== name;
    });
    var body = this.sheet.querySelector('[data-lm-body]');
    if (body) body.scrollTop = 0;
  };

  SizeGuide.prototype.open = function () {
    if (this.dialog.open) return;
    document.documentElement.classList.add('lm-sg-lock');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    // Pre-fill any empty fields with measurements saved on this device.
    var saved = store();
    Array.prototype.forEach.call(this.sheet.querySelectorAll('[data-lm-measure]'), function (input) {
      var m = input.getAttribute('data-lm-measure');
      if (input.value === '' && isNum(saved[m])) input.value = saved[m];
    });
    var active = this.sheet.querySelector('[data-lm-tab][aria-selected="true"]');
    if (active) active.focus();
  };

  SizeGuide.prototype.close = function () {
    if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
    else this.dialog.removeAttribute('open');
  };

  SizeGuide.prototype.bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.open();
    });

    this.dialog.addEventListener('close', function () {
      if (!document.querySelector('.lm-sg-dialog[open]')) document.documentElement.classList.remove('lm-sg-lock');
      self.trigger.focus({ preventScroll: true });
    });

    // Click on the backdrop closes the dialog
    this.dialog.addEventListener('click', function (e) {
      if (e.target === self.dialog) self.close();
    });

    this.sheet.addEventListener('click', function (e) {
      var t = e.target.closest('button');
      if (!t || !self.sheet.contains(t)) return;

      if (t.hasAttribute('data-lm-close')) { self.close(); return; }
      if (t.hasAttribute('data-lm-tab')) { self.selectTab(t.getAttribute('data-lm-tab'), false); return; }

      if (t.hasAttribute('data-lm-cat')) {
        self.category = t.getAttribute('data-lm-cat');
        self.renderGuide();
        var pill = self.panel('guide').querySelector('[data-lm-cat="' + self.category + '"]');
        if (pill) pill.focus();
        // Without product data, the finder follows the chosen chart.
        if (!(self.fit && measuredKeys(self.fit).length)) self.renderFinder();
        return;
      }

      if (t.hasAttribute('data-lm-unit')) {
        self.unit = t.getAttribute('data-lm-unit');
        var saved = store(); saved.unit = self.unit; store(saved);
        self.renderGuide();
        var u = self.panel('guide').querySelector('[data-lm-unit="' + self.unit + '"]');
        if (u) u.focus();
      }
    });

    // Arrow-key navigation between tabs
    this.sheet.addEventListener('keydown', function (e) {
      var tab = e.target.closest && e.target.closest('[data-lm-tab]');
      if (!tab) return;
      var order = ['guide', 'finder', 'measure'];
      var i = order.indexOf(tab.getAttribute('data-lm-tab'));
      if (e.key === 'ArrowRight') { e.preventDefault(); self.selectTab(order[(i + 1) % order.length], true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.selectTab(order[(i + order.length - 1) % order.length], true); }
    });

    this.sheet.addEventListener('submit', function (e) {
      if (!e.target.matches('[data-lm-form]')) return;
      e.preventDefault();
      e.stopPropagation();
      self.onSubmit();
    });
  };

  /* ---------- bootstrap ---------- */
  function cleanOrphans() {
    Array.prototype.forEach.call(document.querySelectorAll('body > .lm-sg-dialog[data-lm-sg-owner]'), function (d) {
      var owner = d.getAttribute('data-lm-sg-owner');
      if (!document.querySelector('[data-lm-size-guide][data-lm-sg-id="' + owner + '"]')) d.remove();
    });
  }

  function initAll() {
    cleanOrphans();
    Array.prototype.forEach.call(document.querySelectorAll('[data-lm-size-guide]:not([data-lm-sg-id])'), function (root) {
      var guide = new SizeGuide(root);
      if (guide.dialog) root.setAttribute('data-lm-sg-id', guide.id);
    });
  }

  window.LMSizeGuide = { initAll: initAll, _recommend: recommend, _normalise: normaliseFit };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Theme editor & quick-view support
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function () { setTimeout(cleanOrphans, 0); });
})();
